import { createContext, useContext, useState } from 'react'

import { StudentEnrollmentValidation_ViewerFragment } from './__generated__/student-enrollment-validation.graphql'
import { DEFAULT_COURSE } from './constants'
import { Steps } from './enums'
import {
  StudentEnrollmentRejectionReason,
  StudentEnrollmentValidationEventData,
  StudentEnrollmentValidationTexts,
} from './types'

export interface SharedDataInterface {
  institution?: string
  enrollmentPeriod?: string
  course?: string
  termsAccepted?: boolean
  documentFile?: File
  documentData?: string
  documentName?: string
  mimeType?: string
  rejectionReason?: StudentEnrollmentRejectionReason
}

export interface ModalContextProps {
  currentStep?: Steps
  setCurrentStep: (currentStep: Steps) => void
  children: React.ReactNode
  eventData: StudentEnrollmentValidationEventData
  viewer: StudentEnrollmentValidation_ViewerFragment | null
  validationOrigin: string
  texts?: StudentEnrollmentValidationTexts
  onAfterValidationSuccess?: () => void | Promise<void>
  initialSharedData?: SharedDataInterface
}

export interface ContextDataInterface {
  currentStep: Steps
  setCurrentStep: (currentStep: Steps) => void
  sharedData: SharedDataInterface
  setSharedData: React.Dispatch<React.SetStateAction<SharedDataInterface>>
  eventData: StudentEnrollmentValidationEventData
  viewer: StudentEnrollmentValidation_ViewerFragment | null
  validationOrigin: string
  texts?: StudentEnrollmentValidationTexts
  onAfterValidationSuccess?: () => void | Promise<void>
}

const ModalContext = createContext<ContextDataInterface | null>(null)

export const useStudentEnrollmentValidationContext = () => {
  const context = useContext(ModalContext)
  if (!context) {
    throw new Error(
      'useStudentEnrollmentValidationContext must be used within StudentEnrollmentValidationContextProvider'
    )
  }
  return context
}

export function StudentEnrollmentValidationContextProvider(props: ModalContextProps) {
  const {
    currentStep = Steps.ACADEMIC_DATA,
    setCurrentStep,
    children,
    viewer,
    eventData,
    validationOrigin,
    texts,
    onAfterValidationSuccess,
    initialSharedData,
  } = props

  const [sharedData, setSharedData] = useState<SharedDataInterface>({
    course: DEFAULT_COURSE,
    ...initialSharedData,
  })

  const contextData = {
    currentStep,
    setCurrentStep,
    sharedData,
    setSharedData,
    eventData,
    viewer,
    validationOrigin,
    texts,
    onAfterValidationSuccess,
  }

  return <ModalContext.Provider value={contextData}>{children}</ModalContext.Provider>
}
